import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useToastContext, setToastRef } from '../../../context/ToastContext'
import { toastConfig } from './ToastRegistry'
import ScoopToast from './ScoopToast'
import styles from './ToastOutlet.module.scss'

/**
 * ToastOutlet - Container component that renders all active toasts
 *
 * This component should be rendered ONCE in App.jsx.
 * It uses a portal to render toasts at the body level.
 * Individual toasts are managed by ToastContext.
 */
export function ToastOutlet() {
    const { toasts, hide, show } = useToastContext()

    // Register ref for non-React contexts (API interceptors)
    useEffect(() => {
        setToastRef({ show, hide })
        return () => setToastRef(null)
    }, [show, hide])

    // Convert toasts object to sorted array (oldest first)
    const toastArray = Object.entries(toasts)
        .map(([id, toast]) => ({ id, ...toast }))
        .sort((a, b) => a.createdAt - b.createdAt)

    // Build position class name
    const positionClass = styles[toastConfig.position.replace('-', '')]

    // Determine stack direction
    const stackClass = toastConfig.stackDirection === 'down' ? styles.stackDown : styles.stackUp

    // Don't render portal if no toasts
    if (toastArray.length === 0) {
        return null
    }

    return createPortal(
        <div
            className={`${styles.toastContainer} ${positionClass} ${stackClass}`}
            aria-live="polite"
            aria-label="Notifications"
        >
            {toastArray.map((toast, index) => (
                <ScoopToast
                    key={toast.id}
                    alert={{ message: toast.message, severity: toast.type }}
                    onClose={() => hide(toast.id)}
                    duration={toast.duration}
                    persistent={toast.persistent}
                    style={{
                        zIndex: 9000 + index,
                        '--toast-index': index,
                    }}
                    // Remove position since container handles it
                    position={{ vertical: 'bottom', horizontal: 'left' }}
                />
            ))}
        </div>,
        document.body
    )
}

export default ToastOutlet
